// useUserAvatar.js — Avatar do user persistido em localStorage.
//
// Sprint v9.12 — Pedro pediu avatar clicável pra upload de foto. Suporta:
//   • Upload via <input type=file> (FileReader → base64 → localStorage)
//   • Default pré-carregado pra Pedro (pedro-avatar.png em assets)
//   • Funciona dark/light mode (sem variant — foto é foto)
//
// API:
//   const { avatarUrl, setAvatar, clearAvatar, isCustom } = useUserAvatar();

import { useEffect, useState, useCallback } from 'react';
import pedroAvatar from '../assets/pedro-avatar.png';

const LS_KEY = 'apolo:user:avatar';
const MAX_BYTES = 2 * 1024 * 1024; // 2MB cap pós-base64 — proteção localStorage

// Defaults pré-carregados por email/name pattern. Adiciona aqui se quiser
// outros users com avatar default.
function defaultForUser(user) {
  if (!user) return null;
  const email = (user.email || '').toLowerCase();
  const name = (user.name || '').toLowerCase();
  if (email.includes('pedromleal') || name.includes('pedro')) return pedroAvatar;
  return null;
}

function readStored() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(LS_KEY);
  } catch {
    return null;
  }
}

export function useUserAvatar(user) {
  const [storedDataUrl, setStoredDataUrl] = useState(readStored);

  // Sincroniza se alterações forem feitas em outras tabs
  useEffect(() => {
    function onStorage(e) {
      if (e.key === LS_KEY) setStoredDataUrl(e.newValue);
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setAvatar = useCallback(async (file) => {
    if (!file) return { ok: false, error: 'no file' };
    if (file.size > MAX_BYTES) {
      return { ok: false, error: `Arquivo > 2MB (${(file.size / 1024 / 1024).toFixed(1)}MB)` };
    }
    // Lê como base64 (data URL)
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    try {
      window.localStorage.setItem(LS_KEY, dataUrl);
      setStoredDataUrl(dataUrl);
      return { ok: true };
    } catch {
      // Quota exceeded — image too big mesmo base64
      return { ok: false, error: 'localStorage cheio — tente foto menor' };
    }
  }, []);

  const clearAvatar = useCallback(() => {
    try {
      window.localStorage.removeItem(LS_KEY);
    } catch {
      // ignore
    }
    setStoredDataUrl(null);
  }, []);

  const fallback = defaultForUser(user);
  const avatarUrl = storedDataUrl || fallback;
  const isCustom = !!storedDataUrl;

  return { avatarUrl, setAvatar, clearAvatar, isCustom };
}
